/* ===========================================================================
   ROVE NEXT — QUANTITY VS. WHAT IS ACTUALLY AVAILABLE. 24 September 2026.

   The buy box always posted quantity: 1 — there was no stepper on the PDP at
   all, only in the cart drawer. This wires up the real one (snippets/
   quantity-selector.liquid, the theme's own component, rendered into the buy
   box by sections/rn-buybox.liquid) against the true ceiling for whatever
   variant is selected: real stock plus whatever pre-order allocation sits
   behind it, e.g. 2 on the shelf and 28 still on the water.

   THREE THINGS THIS DOES, ALL FROM THE SAME TWO NUMBERS:
     1. Raises/lowers the stepper's max per variant.
     2. If the quantity chosen spans both pools, warns that the whole order
        becomes a pre-order and ships together once the pre-order items
        arrive, and that staying at or under real stock ships it now.
     3. Blocks Add to Cart outright if the quantity exceeds the true total,
        and says what the real number is — a deliberate exception to how
        assets/rn-preorder-cap.js handles the cart (correct after the fact,
        never block). That script is about protecting a shipment nobody has
        confirmed yet; this is a number the shopper can see and fix before
        they ever submit, so blocking here costs nothing and prevents the
        correction from having to happen at all.

   WHERE THE NUMBERS COME FROM. window.RN_NOTIFY, rendered per-variant by
   snippets/rn-preorder-cap.liquid from variant.inventory_quantity and
   metafields.preorder.remaining — reused rather than re-fetched, since that
   block already exists on every product page. `stock: null` there means
   Shopify is not tracking a real count (inventory_management isn't
   'shopify'); this file treats that as "unknown" and gets out of the way
   entirely for that variant, rather than showing a number that is not real
   -- unless it has a pre-order allocation, in which case
   the allocation is the cap (see total()).

   REYA IS COVERED TOO, since 26 September 2026. Its config-grid script (the
   cfg_on branch in sections/rn-buybox.liquid) now fires the same
   rn:bb-variant event, and bootstrap() below reads the form's variant in
   case that happened before this file loaded. Until then Reya had no
   quantity check at all: `current` stayed null and everything failed open.
   The pre-order banner/button switching (paintPreorder) stays off there --
   the grid script owns those elements.
   =========================================================================== */
(function () {
  var bb = document.querySelector('[id^="rn-bb-"]');
  if (!bb) return;

  var qtyRoot = bb.querySelector('[data-bb-qty] quantity-selector-component');
  var stockline = bb.querySelector('[data-bb-stockline]');
  var mirror = bb.querySelector('[data-bb-qty-mirror]');
  var msg = bb.querySelector('[data-bb-msg]');
  if (!qtyRoot) return; /* no real selector on this page — nothing to wire up */

  var qtyInput = qtyRoot.querySelector('input[name="quantity"]');

  var LOW = 10; /* below this many REAL units on hand, the line names the exact count as low stock */

  var variants = {};
  (function readVariants() {
    var n = window.RN_NOTIFY;
    if (!n || !Array.isArray(n.variants)) return;
    n.variants.forEach(function (v) { variants[String(v.id)] = v; });
  })();

  var current = null; /* the RN_NOTIFY record for whichever variant is selected now */

  /* HOW MANY OF EACH VARIANT ARE ALREADY IN THE CART. 25 Sept 2026.

     THE BUG THIS CLOSES. The stepper's own max is a flat ceiling on ONE Add
     to Cart action -- it says nothing about what already happened. Add 60 of
     a variant whose true total is 60, and the cart is exactly full; add 60
     again without reloading the page (this theme opens the cart drawer, it
     never reloads) and nothing here or in the quantity-selector-component's
     own clamping catches it, because BOTH read data-cart-quantity, and that
     attribute is rendered ONCE from the server at page load and never
     refreshed afterward. It still says 0 for the rest of the visit, so a
     second full-total add looks, to every check available, identical to the
     first one, briefly reaching checkout too, until assets/rn-preorder-
     cap.js's own after-the-fact correction catches up.

     A SECOND BUG FOUND WITHIN HOURS OF FIXING THE FIRST ONE: this used to be
     one shared `cartQty` number, refreshed for whichever variant was current
     at the moment refreshCartQty() was called. Confirmed live: add 60 Oat,
     41 Dune and 3 Moonstone in three separate visits to their swatches, then
     go back to Dune and add 1 more. If that add happens before the fetch
     triggered by switching BACK to Dune resolves, `cartQty` can still be
     holding Moonstone's number (3) from the previous switch -- so Dune's
     remaining() comes out as 41 - 3 = 38 instead of 41 - 41 = 0, and the add
     goes through on a different variant's leftover number entirely.

     Keyed by variant id instead, and always populated in full from ONE
     fetch, so remaining() reads the number for whichever variant it is
     actually asked about -- correct regardless of which variant a fetch
     already in flight happened to be triggered by. */
  var cartQtyMap = {};
  var cartQtyReady = null; /* the in-flight refresh, if any -- see the submit guard below */
  var cartQtyLoadedOnce = false; /* true once real data has landed at least once */
  function refreshCartQty() {
    cartQtyReady = fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        cartQtyMap = {};
        if (!cart || !cart.items) return;
        cart.items.forEach(function (item) {
          var id = String(item.variant_id);
          cartQtyMap[id] = (cartQtyMap[id] || 0) + item.quantity;
        });
        cartQtyLoadedOnce = true;
        if (qtyInput && current) {
          qtyInput.setAttribute('data-cart-quantity', String(cartQtyMap[String(current.id)] || 0));
        }
      })
      .catch(function () { /* leave cartQtyMap at whatever it last was rather than guess */ })
      .then(function () { cartQtyReady = null; });
    return cartQtyReady;
  }

  /* EVERY PRODUCT FOLLOWS THE SAME RULE; preorder.mode is never consulted.
     26 September 2026. A variant Shopify is not tracking (stock null) has
     nothing countable on the shelf, so it is treated as 0 in stock: its
     ceiling is its pre-order allocation alone. It used to return null here --
     no cap at all -- so any quantity went into the cart and rn-preorder-cap.js
     shrank the line to the allocation afterwards, with a toast. Untracked
     with no allocation is still uncapped: there is no number to enforce. */
  function total(v) {
    if (!v) return null;
    if (v.stock === null || v.stock === undefined) {
      return v.preorderRemaining > 0 ? v.preorderRemaining : null;
    }
    return Math.max(0, v.stock) + (v.preorderRemaining || 0);
  }

  /* What is left to add for THIS variant specifically, given what is
     already sitting in the cart for it -- the number the hard stop below
     actually enforces, as distinct from `total`, which is the product's own
     ceiling and stays the same regardless of anyone's cart. */
  function remaining(v) {
    var t = total(v);
    if (t === null || !v) return t;
    return Math.max(0, t - (cartQtyMap[String(v.id)] || 0));
  }

  function currentQty() {
    var n = parseInt(qtyInput && qtyInput.value, 10);
    return isFinite(n) && n > 0 ? n : 1;
  }

  /* KEEP WHAT THE SHOPPER TYPED. Type 100 into a field capped at 60 and
     component-quantity-selector.js's own setQuantity() (run on blur, which
     clicking Add to Cart triggers) silently rewrites it to 60. We don't want
     the number changed behind their back: it stays as typed, the box goes
     red, the line above the button says what is actually available, and Add
     to Cart is disabled until they lower it.

     Overridden on THIS instance only (the component dispatches on:blur to
     instance[method], so an own property wins), so the cart drawer's
     steppers keep their normal clamping. An over-max number skips the clamp
     but still announces the change; everything else -- below min, empty,
     off-step -- goes through the component's own handling untouched. */
  qtyRoot.setQuantity = function (event) {
    var n = parseInt(event && event.target && event.target.value, 10);
    var eff = typeof this.getEffectiveMax === 'function' ? this.getEffectiveMax() : null;
    if (isFinite(n) && eff !== null && n > eff) {
      event.preventDefault();
      this.onQuantityChange();
      this.updateButtonStates();
      return;
    }
    var base = Object.getPrototypeOf(this).setQuantity;
    if (typeof base === 'function') return base.apply(this, arguments);
  };

  /* "+" AT THE CEILING SAYS WHY INSTEAD OF GOING DEAD. 29 September 2026.
     Typing past what is available showed the message, but the "+" button
     never could: the component disables it the moment the value reaches its
     effective max, and a disabled button fires no click, so pressing it did
     nothing at all with no explanation. Here "+" stays enabled (unless the
     server disabled it, or the variant is sold out), and a press that would
     go past what is left leaves the number where it is and puts "Only N
     available" in the line above the button. It is a notice, not the typed-
     over-max error: the quantity shown is still valid, so Add to Cart stays
     usable. Over-max values already show the error and are left to it. */
  qtyRoot.updateButtonStates = function () {
    var base = Object.getPrototypeOf(this).updateButtonStates;
    if (typeof base === 'function') base.apply(this, arguments);
    var plus = this.refs && this.refs.plusButton;
    if (plus && !this.serverDisabledPlus && !soldOut(current)) plus.disabled = false;
  };
  qtyRoot.increaseQuantity = function (event) {
    var left = remaining(current);
    var cur = typeof this.getCurrentValues === 'function' ? this.getCurrentValues() : null;
    if (cur && left !== null && left > 0 && cur.value <= left && cur.value + (cur.step || 1) > left) {
      if (event) event.preventDefault();
      showCap(left, current);
      return;
    }
    var base = Object.getPrototypeOf(this).increaseQuantity;
    if (typeof base === 'function') return base.apply(this, arguments);
  };
  function showCap(left, v) {
    if (!stockline || !v) return;
    var inCart = cartQtyMap[String(v.id)] || 0;
    stockline.textContent = inCart > 0
      ? 'You’ve reached the maximum. Only ' + left + ' more available, since you already have ' +
        inCart + ' in your cart.'
      : 'You’ve reached the maximum. Only ' + left + ' available for this item.';
    stockline.classList.remove('warn', 'low');
    stockline.classList.add('err');
    stockline.hidden = false;
  }
  /* The component may have disabled "+" before this file loaded. */
  if (qtyRoot.refs && qtyRoot.refs.plusButton) qtyRoot.updateButtonStates();

  /* Every keystroke re-checks, so the error shows while they type, not only
     after the field loses focus. */
  if (qtyInput) {
    /* Otherwise Enter in an over-max field gets the browser's own "must be
       less than or equal to" bubble instead of our message. */
    if (qtyInput.form) qtyInput.form.noValidate = true;
    qtyInput.addEventListener('input', function () {
      syncMirror(currentQty());
      updateStockline(currentQty(), current);
    });
  }

  /* The error state: red box around the stepper, and every Add to Cart in
     the buy box (main + sticky) disabled. `blocked` remembers that WE
     disabled them, so clearing the error never re-enables a button that is
     disabled for a real reason (sold out, unresolved configuration). */
  var atcBtns = Array.prototype.slice.call(bb.querySelectorAll('[data-bb-form] [type="submit"]'));
  var blocked = false;
  var prevDisabled = [];
  /* NOTHING LEFT AT ALL IS SOLD OUT, NOT A QUANTITY ERROR. 26 September 2026.
     A tracked variant with 0 on the shelf and no pre-order allocation has a
     total of 0, and the over-check used to read that as "the cart already
     holds everything" -- "You already have all 0 available in your cart."
     Shopify can still call such a variant `available` (its inventory policy
     left on CONTINUE), which also kept rn-notify.js's sign-up form away. So a
     total of 0, or Shopify's own available:false, is sold out here: the
     button is disabled and says so, no quantity error, and rn-notify.js
     (same rule) offers the email sign-up. */
  function soldOut(v) {
    return !!v && (v.available === false || total(v) === 0);
  }

  /* `over` = red box on the stepper; either reason blocks the buttons. */
  var soldLabels = null; /* labels this file replaced with the sold-out one */
  function setOver(over, sold) {
    qtyRoot.classList.toggle('is-error', !!over);
    if (qtyInput) {
      if (over) qtyInput.setAttribute('aria-invalid', 'true');
      else qtyInput.removeAttribute('aria-invalid');
    }
    var block = !!(over || sold);
    if (block && !blocked) {
      prevDisabled = atcBtns.map(function (b) { return b.disabled; });
      atcBtns.forEach(function (b) { b.disabled = true; });
      blocked = true;
    } else if (!block && blocked) {
      atcBtns.forEach(function (b, i) { b.disabled = !!prevDisabled[i]; });
      blocked = false;
    }
    var SOLD = (window.RN_BB_LABELS || {}).sold;
    if (sold && SOLD && !soldLabels) {
      soldLabels = labelBtns.map(function (b) { return b.textContent; });
      labelBtns.forEach(function (b) { b.textContent = SOLD; });
    } else if (!sold && soldLabels) {
      labelBtns.forEach(function (b, i) { if (soldLabels[i] != null) b.textContent = soldLabels[i]; });
      soldLabels = null;
    }
  }

  /* ---- PRE-ORDER IS DECIDED BY THE QUANTITY, NOT BY preorder.mode --------
     The page is either shipping now or a pre-order, and which one depends
     on how many the shopper wants against what is really on the shelf:

       qty <= stock                -> Add to Cart, "Ships directly to your
                                      door" and its timeline, no pre-order
                                      date on the order
       qty >  stock (incl. 0 left) -> PRE-ORDER, the pre-order banner with
       and a container date           the arrival window, and that date +
                                      container ref ride onto the order --
                                      the whole order ships together once
                                      the pre-order items arrive

     A count Shopify is not tracking (stock null) counts as 0 on the shelf;
     preorder.mode is never consulted, for any product. The server renders
     the quantity-1 answer with the same rule (po_on in
     sections/rn-buybox.liquid); this keeps it true as the quantity moves.

     Not on the config grid (Reya): its own script owns these elements. */
  var isCfg = !!bb.querySelector('[data-bb-cfg]');
  function all(sel) { return Array.prototype.slice.call(bb.querySelectorAll(sel)); }
  var poEls = all('[data-bb-po]');
  var poSubs = all('[data-bb-posub]');
  var poProps = all('[data-bb-form] [data-bb-poprop]');
  var poIsos = all('[data-bb-form] [data-bb-poiso]');
  var poRefs = all('[data-bb-form] [data-bb-poref]');
  var labelBtns = all('.bb-atc,.bb-sticky-atc');
  var shipBox = bb.querySelector('.bb-ship[data-bb-ship]');
  var tlBox = bb.querySelector('.bb-tl[data-bb-tl]');
  var LABELS = window.RN_BB_LABELS || {};
  var PO_M = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep',
    'Oct', 'Nov', 'Dec'];

  function isPreorderAt(qty, v) {
    if (!v || !v.eta) return false;
    /* untracked = nothing on the shelf, so any quantity is a pre-order */
    return qty > Math.max(0, v.stock || 0);
  }

  /* The same ship window the server prints: warehouse date + buffer is the
     early end, `window` days after that the late end. {text, iso}, e.g.
     {"Dec 1–8", "2026-12-08"}. */
  function poAdd(iso, days) {
    var p = String(iso).split('-');
    if (p.length !== 3) return null;
    var d = new Date(Date.UTC(+p[0], +p[1] - 1, +p[2]));
    if (isNaN(d.getTime())) return null;
    d.setUTCDate(d.getUTCDate() + days);
    return d;
  }
  function pad(n) { return (n < 10 ? '0' : '') + n; }
  function poShift(iso, buf, win) {
    var from = poAdd(iso, buf);
    if (!from) return null;
    var to = poAdd(iso, buf + win);
    var text = PO_M[from.getUTCMonth()] + ' ' + from.getUTCDate() + '–' +
      (from.getUTCMonth() === to.getUTCMonth() ? '' : PO_M[to.getUTCMonth()] + ' ') + to.getUTCDate();
    return { text: text, iso: to.getUTCFullYear() + '-' + pad(to.getUTCMonth() + 1) + '-' + pad(to.getUTCDate()) };
  }
  function intAttr(el, name, dflt) {
    var n = parseInt(el.getAttribute(name), 10);
    return isNaN(n) ? dflt : n;
  }
  /* A date still estimated from a batch (eta_type 'estimated') gets the wider
     window; a loaded container, or no type at all, the normal one. */
  function winFor(el, etaType) {
    if (etaType === 'estimated') return el ? intAttr(el, 'data-po-window-batch', 12) : 12;
    return el ? intAttr(el, 'data-po-window', 7) : 7;
  }

  function paintPreorder(qty, v) {
    if (isCfg || !v) return;
    var on = isPreorderAt(qty, v);
    var shown = null;
    poEls.forEach(function (el) {
      el.hidden = !on;
      if (!on) return;
      var got = poShift(v.eta, intAttr(el, 'data-po-buffer', 2), winFor(el, v.etaType));
      if (!got) return;
      shown = got;
      var b = el.querySelector('[data-bb-po-tx] b');
      if (b) b.textContent = got.text;
    });
    poSubs.forEach(function (el) { el.hidden = !on; });
    /* nothing to ship on a pre-order (the date says when) or when sold out */
    var noShip = on || soldOut(v);
    if (shipBox) shipBox.hidden = noShip;
    if (tlBox) tlBox.hidden = noShip;
    /* the promise that rides onto the order is exactly the one on screen */
    poProps.forEach(function (inp) { inp.value = on && shown ? shown.text : ''; });
    poIsos.forEach(function (inp) { inp.value = on && shown ? shown.iso : ''; });
    poRefs.forEach(function (inp) { inp.value = on ? (v.poRef || '') : ''; });
    /* The label only -- whether the button is enabled is rn-pdp-variant.js's
       call (sold out) and setOver()'s (too many), not this one's. */
    if (v.available && !soldOut(v)) {
      var want = on ? LABELS.po : LABELS.atc;
      if (want) labelBtns.forEach(function (b) { if (b.textContent.trim() !== want) b.textContent = want; });
    }
  }

  /* THE PRE-ORDER LINE PROPERTIES FOR ANY CONTAINER DATE, for other scripts --
     assets/rn-pdp-addon.js, so a Bundle and save add-on on pre-order goes into
     the cart carrying exactly what the buy box's own pre-order line carries:
     the ship window the shopper would see (same buffer and window as the
     banner), its late end as _preorder_eta, and the container as
     _preorder_ref. The property name is the buy box's own (its po_prop
     setting). etaType picks the batch or container window. null if the date
     is bad. */
  window.RN_PO_PROPS = function (eta, ref, etaType) {
    var el = poEls[0] || null;
    var buf = el ? intAttr(el, 'data-po-buffer', 2) : 2;
    var win = winFor(el, etaType);
    var got = eta ? poShift(eta, buf, win) : null;
    if (!got) return null;
    var m = poProps[0] ? /^properties\[(.+)\]$/.exec(poProps[0].getAttribute('name') || '') : null;
    var props = {};
    props[m ? m[1] : 'Estimated to ship'] = got.text;
    props._preorder_eta = got.iso;
    if (ref) props._preorder_ref = ref;
    return props;
  };

  function syncMirror(qty) {
    if (mirror) mirror.value = String(qty);
  }

  function applyMax(t) {
    if (!qtyRoot || typeof qtyRoot.updateConstraints !== 'function') return;
    var cur = qtyRoot.getCurrentValues ? qtyRoot.getCurrentValues() : { min: 1, step: 1 };
    var min = cur.min || 1;
    /* Belt and braces against the exact bug just found: min must never sit
       above max, or the browser refuses the input outright rather than
       just disabling it. t is expected to always be >= 1 here (see the
       comment above refresh()), but nothing stops that assumption from
       becoming false somewhere this file cannot see. */
    if (t !== null && t < min) min = t;
    /* updateConstraints() clamps the value into range too. Put back a number
       that was only "wrong" by being too high, so it stays as typed and
       shows as an error instead of silently changing. */
    var before = qtyInput ? parseInt(qtyInput.value, 10) : NaN;
    qtyRoot.updateConstraints(String(min), t === null ? null : String(t), String(cur.step || 1));
    if (qtyInput && isFinite(before) && before > parseInt(qtyInput.value, 10)) {
      qtyInput.value = String(before);
      if (typeof qtyRoot.updateButtonStates === 'function') qtyRoot.updateButtonStates();
    }
    syncMirror(currentQty());
  }

  /* THE MIXED-ORDER WARNING. Only when the chosen quantity spans real stock
     AND the pre-order allocation behind it. A mixed order is NOT split: it
     ships all together once the pre-order units arrive, so the whole order is
     effectively a pre-order -- and the shopper has to be told that before
     they add it, along with the one way to get it shipped now: stay at or
     under what is actually on the shelf.

     With nothing on the shelf there is no line at all: the whole order is
     plainly a pre-order and the PRE-ORDER button and arrival banner say so. */
  function splitPhrase(qty, v, t) {
    var stock = v ? Math.max(0, v.stock || 0) : 0;
    if (!v || !(v.preorderRemaining > 0) || stock <= 0 || qty <= stock || qty > t) return '';
    return 'Only ' + stock + ' in stock, so all ' + qty +
      ' will ship together once the pre-order items arrive. To ship now, lower the quantity to ' +
      stock + ' or fewer.';
  }

  /* Over what can actually be added (what is left after this variant's cart
     contents): the error, which takes the line over entirely. */
  function overPhrase(qty, v) {
    var left = remaining(v);
    if (left === null || qty <= left) return '';
    var inCart = cartQtyMap[String(v.id)] || 0;
    if (left <= 0) return 'You already have all ' + inCart + ' available in your cart.';
    if (inCart > 0) {
      return 'Only ' + left + ' more available (you already have ' + inCart +
        ' in your cart). Lower the quantity to ' + left + ' or fewer to add to cart.';
    }
    return 'Only ' + left + ' available. Lower the quantity to ' + left + ' or fewer to add to cart.';
  }

  /* THREE STATES, per the ticket -- based on REAL STOCK, not the combined
     total: 10+ real units says "In Stock" with no number; under 10 names the
     exact count so it reads as genuine urgency, not invented; zero real units
     says nothing here at all, because the PRE-ORDER button and the
     arrival-date banner already carry that message on their own. The
     mixed-order warning overrides all three when the chosen quantity runs
     past real stock -- a variant with 15 in stock is "In Stock" at rest, but
     choosing 20 makes it a pre-order, and "In Stock" beside that would
     contradict it. The warning already names the stock count itself. And
     over what is available at all, the error replaces every one of them. */
  function updateStockline(qty, v) {
    var sold = soldOut(v);
    var over = sold ? '' : overPhrase(qty, v);
    paintPreorder(qty, v);
    setOver(!!over, sold);
    /* On Reya's grid paintPreorder() stays out and the grid script sets the
       ship card from pre-order state alone; hide it when sold out there too.
       The grid re-sets it on every variant change, so no restore needed. */
    if (isCfg && sold) {
      if (shipBox) shipBox.hidden = true;
      if (tlBox) tlBox.hidden = true;
    }
    if (!stockline) return;
    if (sold) {
      stockline.hidden = true;
      stockline.textContent = '';
      stockline.classList.remove('err', 'warn', 'low', 'ok');
      return;
    }
    if (over) {
      stockline.textContent = over;
      stockline.classList.remove('warn', 'low', 'ok');
      stockline.classList.add('err');
      stockline.hidden = false;
      return;
    }
    stockline.classList.remove('err');
    var t = total(v);
    var stock = v ? Math.max(0, v.stock || 0) : 0;
    var lead = '';
    if (t !== null && t > 0) {
      if (stock >= LOW) lead = 'In Stock';
      else if (stock > 0) lead = 'Low stock — only ' + stock + ' left';
    }
    var split = splitPhrase(qty, v, t);
    var text = split || lead;
    if (!text) {
      stockline.hidden = true;
      stockline.textContent = '';
      return;
    }
    stockline.textContent = text;
    /* The warning goes in an orange box (.warn in rn-buybox.liquid) led by a
       bold "Pre-order:", so it cannot be skimmed past on the way to the button. */
    if (split) {
      var tag = document.createElement('b');
      tag.textContent = 'Pre-order: ';
      stockline.insertBefore(tag, stockline.firstChild);
    }
    stockline.classList.toggle('warn', !!split);
    stockline.classList.toggle('low', !split && stock > 0 && stock < LOW);
    stockline.classList.toggle('ok', !split && stock >= LOW);
    stockline.hidden = false;
  }

  /* applyMax() GETS THE FLAT TOTAL, NOT remaining(). 25 September 2026 -- a
     bug found within hours of the fix above it. remaining() can genuinely be
     0 (the cart already holds everything there is), and passing 0 straight
     into the max attribute while min stayed at its default of 1 gave the
     browser min="1" max="0" -- an impossible range, and Chrome's own
     validation bubble said so: "Minimum value (1) must be less than the
     maximum value (0)."

     The total, by contrast, is never below 1 for anything actually
     purchasable -- a variant with nothing left at all already has can_buy
     false and the whole form disabled -- so it can never contradict min.
     component-quantity-selector.js's OWN getEffectiveMax() already
     subtracts data-cart-quantity from whatever max it is given (that is
     the entire reason that attribute exists), and refreshCartQty() below
     keeps it correct. Passing remaining() here as well would subtract the
     cart's contents TWICE.

     The hard stop further down still checks remaining() directly and does
     not go anywhere near these HTML attributes, so it is unaffected by any
     of this — it is what actually enforces the number, not the stepper's
     own clamping. The stock LINE also keeps showing the flat total, per the
     ticket -- what is already in someone's cart is not the shopper's
     business when they are just reading how much of the product exists. */
  function refresh() {
    applyMax(total(current));
    updateStockline(currentQty(), current);
  }

  /* A quantity chosen for one variant should not silently carry over to the
     next one -- 60 of Oat left sitting in the box after switching to Dune
     (its own total 41) reads as a leftover mistake, not a deliberate choice.
     Reset to whatever this variant's own min is (ordinarily 1) on every
     switch, the same moment its cap is recalculated. */
  function resetQty() {
    if (!qtyInput) return;
    var min = qtyRoot.getCurrentValues ? (qtyRoot.getCurrentValues().min || 1) : 1;
    qtyInput.value = String(min);
    if (typeof qtyRoot.updateButtonStates === 'function') qtyRoot.updateButtonStates();
    syncMirror(min);
  }

  document.addEventListener('rn:bb-variant', function (e) {
    var id = e && e.detail && e.detail.id;
    current = id != null ? (variants[String(id)] || null) : null;
    /* rn-pdp-variant.js has just set every button's disabled state for the
       new variant, so whatever setOver() remembered is stale -- forget it
       rather than restore it over the top. */
    blocked = false;
    prevDisabled = [];
    soldLabels = null;
    resetQty();
    /* Straight away, not after the cart fetch below: rn-pdp-variant.js has
       just painted the quantity-1 label, and the banner/ship box -- and a
       sold-out state -- should switch with it rather than a beat later. */
    updateStockline(currentQty(), current);
    refreshCartQty().then(refresh);
  });

  /* START FROM THE VARIANT ALREADY IN THE FORM. The rn:bb-variant above can
     fire before this deferred file has loaded -- Reya's config-grid script is
     inline and resolves its variant during parsing -- and a missed event
     meant `current` stayed null, which fails open: no cap at all. The form's
     id field always holds the live variant (the server's, or whatever a
     script has written since), so read it once now; a later event simply
     replaces it. */
  (function bootstrap() {
    var idEl = bb.querySelector('[data-bb-form] input[name="id"]');
    var v = idEl && idEl.value ? variants[String(idEl.value)] : null;
    if (!v || current) return;
    current = v;
    updateStockline(currentQty(), current);
    refreshCartQty().then(refresh);
  })();

  document.addEventListener('quantity-selector:update', function (e) {
    if (!qtyRoot.contains(e.target) && e.target !== qtyRoot) return;
    var qty = (e.detail && typeof e.detail.quantity === 'number') ? e.detail.quantity : currentQty();
    syncMirror(qty);
    updateStockline(qty, current);
  });

  /* A successful add just changed how much of THIS variant is in the cart,
     so the ceiling for the NEXT add has to be re-read rather than reused --
     this is the exact gap that let a second full add through. rn-buybox.
     liquid's own add-to-cart handler dispatches this on every success. */
  document.addEventListener('cart:update', function () {
    refreshCartQty().then(refresh);
  });

  /* THE DRAWER DOES NOT FIRE cart:update. Changing or removing a line there
     goes through component-cart-items.js, which fires Shopify's standard
     'shopify:cart:lines-update' instead -- so removing an item left this
     page still counting it as in the cart, and still refusing a quantity
     the shopper could now have. Its `promise` settles once the cart has
     actually changed; re-read after that (or straight away if it has none),
     and re-check whatever is in the box now against the new number. */
  document.addEventListener('shopify:cart:lines-update', function (e) {
    var p = e && e.promise;
    var after = function () { refreshCartQty().then(refresh); };
    if (p && typeof p.then === 'function') p.then(after, after);
    else after();
  });

  /* ---- the hard stop -----------------------------------------------------
     Capture phase, on both forms, so this runs before rn-buybox.liquid's own
     submit handler ever starts the add-to-cart fetch. Checked against what
     is LEFT after the cart's own current contents, not the flat total --
     otherwise a shopper who already has the full amount in their cart could
     add the full amount again, which is exactly how 28 became 56. current
     === null means the variant sync (rn-pdp-variant.js) has not reported in
     yet, or this is Reya, which never fires it — fail open rather than
     block on a number we do not actually have.

     BLOCKING WHENEVER cartQtyReady WAS TRUTHY WAS TOO AGGRESSIVE. 25 Sept
     2026, found within the hour: a refresh fires on every variant switch AND
     every successful add, so on an ordinary connection a shopper doing
     nothing unusual at all — switch a swatch, add to cart, try to add again
     — could hit "still checking" repeatedly, for a gap that is normally
     gone well before a human clicks anything. Blocking here now only
     happens before the FIRST fetch of the visit has ever landed, which is
     the one moment cartQtyMap could plausibly still be an empty guess
     rather than the real cart. Every refresh after that keeps the map
     correct quickly enough that a submission mid-refresh is safe to check
     against whatever it already holds. */
  Array.prototype.slice.call(bb.querySelectorAll('[data-bb-form]')).forEach(function (form) {
    form.addEventListener('submit', function (e) {
      if (cartQtyReady && !cartQtyLoadedOnce) {
        e.preventDefault();
        e.stopImmediatePropagation();
        if (msg) {
          msg.textContent = 'Still checking what is in your cart — try Add to Cart again in a moment.';
          msg.className = 'bb-cartmsg err';
        }
        return;
      }

      var left = remaining(current);
      if (left === null) return;
      /* The box is never clamped any more (see setQuantity above), so what
         it shows IS what the shopper asked for. The button is already
         disabled in this state; this catches Enter pressed in the field. */
      var qty = currentQty();
      if (qty <= left) return;

      e.preventDefault();
      e.stopImmediatePropagation();
      /* Their number stays; the red box and the line above the button
         already say what is wrong and what to change it to. */
      updateStockline(qty, current);
      if (qtyInput) {
        try { qtyInput.focus(); } catch (err) {}
      }
    }, true);
  });
})();
